<template>
  <v-card outlined>
    <v-card-subtitle>Package catalog</v-card-subtitle>
    <v-card-text>
      <v-alert v-if="!xanoApiBase" type="info" outlined dense>
        Open this page from Revit to load the package catalog.
      </v-alert>

      <template v-else>
        <v-row dense align="center" class="mb-1">
          <v-col cols="12" sm="8">
            <v-text-field
              v-model="search"
              outlined
              dense
              hide-details
              clearable
              label="Search packages"
              prepend-inner-icon="mdi-magnify"
            />
          </v-col>
          <v-col cols="12" sm="4" class="text-sm-right">
            <v-btn small outlined :loading="loading" @click="loadPackages">Refresh</v-btn>
          </v-col>
        </v-row>

        <v-chip-group v-if="categories.length > 0" v-model="selectedCategory" column>
          <v-chip filter outlined :value="null" small>All</v-chip>
          <v-chip v-for="category in categories" :key="category" filter outlined :value="category" small>
            {{ category }}
          </v-chip>
        </v-chip-group>

        <v-progress-linear v-if="loading" indeterminate class="mt-2" />

        <v-alert v-if="loadError" type="error" outlined dense class="mt-2">
          Failed to load packages: {{ loadError }}
        </v-alert>

        <v-alert v-else-if="!loading && filteredPackages.length === 0" type="info" outlined dense class="mt-2">
          {{ packages.length === 0 ? "No packages published yet." : "No packages match the filters." }}
        </v-alert>

        <v-row v-if="!loadError" dense class="mt-1">
          <v-col v-for="pkg in filteredPackages" :key="pkg.packageId + '@' + pkg.version" cols="12" sm="6" md="4">
            <v-card outlined class="d-flex flex-column package-tile" height="100%" @click="openDetails(pkg)">
              <span class="package-tile__corner package-tile__corner--tl" />
              <span class="package-tile__corner package-tile__corner--br" />
              <span class="package-tile__hint-tap">Click for details</span>
              <v-img v-if="pkg.iconUrl" :src="pkg.iconUrl" height="120" contain class="mt-2 package-tile__icon" />
              <div class="px-4 pt-4 pb-1 package-tile__title">
                {{ pkg.displayName || pkg.packageId }}
              </div>
              <div class="px-4 pb-2 package-tile__meta">
                <span class="package-tile__version">v{{ pkg.version }}</span>
                <span v-if="pkg.category" class="package-tile__divider">/</span>
                <span v-if="pkg.category">{{ pkg.category }}</span>
                <span v-if="(pkg.supportedRevitVersions || []).length > 0" class="package-tile__divider">/</span>
                <span v-if="(pkg.supportedRevitVersions || []).length > 0">
                  Revit {{ pkg.supportedRevitVersions.join(", ") }}
                </span>
              </div>
              <v-card-text class="flex-grow-1 pt-1">
                <div v-if="pkg.description" class="mb-2 package-tile__desc">{{ pkg.description }}</div>
                <v-chip v-for="tag in pkg.tags || []" :key="tag" x-small outlined class="mr-1 mb-1 package-tile__tag">
                  {{ tag }}
                </v-chip>
              </v-card-text>
              <v-card-actions class="package-tile__actions">
                <v-tooltip top :disabled="incompatibleVersionsFor(pkg).length === 0">
                  <template #activator="{ on, attrs }">
                    <span v-on="on" v-bind="attrs">
                      <v-btn
                        text
                        class="package-tile__run"
                        :disabled="commandTargets.length === 0 || incompatibleVersionsFor(pkg).length > 0"
                        @click.stop="runPackage(pkg)"
                      >
                        Run<span class="package-tile__run-arrow">&rarr;</span>
                      </v-btn>
                    </span>
                  </template>
                  <span> Not compatible with Revit {{ incompatibleVersionsFor(pkg).join(", ") }} </span>
                </v-tooltip>
                <span v-if="commandTargets.length === 0" class="caption text--secondary package-tile__hint">
                  Select a Revit instance
                </span>
              </v-card-actions>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </v-card-text>

    <v-dialog
      :value="!!detailsPkg"
      max-width="880"
      scrollable
      content-class="package-dialog"
      @input="
        (v) => {
          if (!v) closeDetails();
        }
      "
    >
      <v-card v-if="detailsPkg" class="package-dialog__card">
        <span class="package-dialog__corner package-dialog__corner--tl" />
        <span class="package-dialog__corner package-dialog__corner--tr" />

        <div class="package-dialog__hero">
          <v-img
            v-if="detailsPkg.iconUrl"
            :src="detailsPkg.iconUrl"
            height="80"
            width="80"
            contain
            class="package-dialog__icon"
          />
          <div class="package-dialog__hero-text">
            <div class="package-dialog__meta">
              <span class="package-dialog__version">v{{ detailsPkg.version }}</span>
              <span v-if="detailsPkg.category" class="package-dialog__divider">/</span>
              <span v-if="detailsPkg.category">{{ detailsPkg.category }}</span>
              <span v-if="(detailsPkg.supportedRevitVersions || []).length > 0" class="package-dialog__divider">/</span>
              <span v-if="(detailsPkg.supportedRevitVersions || []).length > 0">
                Revit {{ detailsPkg.supportedRevitVersions.join(", ") }}
              </span>
            </div>
            <h2 class="package-dialog__title">{{ detailsPkg.displayName || detailsPkg.packageId }}</h2>
            <div class="package-dialog__tags">
              <v-chip v-for="tag in detailsPkg.tags || []" :key="tag" x-small outlined class="package-tile__tag mr-1">
                {{ tag }}
              </v-chip>
            </div>
          </div>
        </div>

        <v-card-text class="package-dialog__body">
          <section class="package-dialog__section">
            <h3 class="package-dialog__h3">Description</h3>
            <p class="package-dialog__text">
              {{ detailsPkg.longDescription || detailsPkg.description || "No description provided." }}
            </p>
          </section>

          <section class="package-dialog__section">
            <h3 class="package-dialog__h3">How to use</h3>
            <ol class="package-dialog__steps">
              <li v-for="(step, idx) in instructionsFor(detailsPkg)" :key="idx" class="package-dialog__step">
                <span class="package-dialog__step-num">{{ String(idx + 1).padStart(2, "0") }}</span>
                <span class="package-dialog__step-text">{{ step }}</span>
              </li>
            </ol>
          </section>

          <section class="package-dialog__section">
            <h3 class="package-dialog__h3">Screenshots</h3>
            <div class="package-dialog__gallery">
              <div
                v-for="(src, idx) in screenshotsFor(detailsPkg)"
                :key="idx"
                class="package-dialog__shot"
                :style="{ backgroundImage: `url(${src})` }"
              />
            </div>
          </section>

          <section class="package-dialog__section">
            <h3 class="package-dialog__h3">Walkthrough</h3>
            <div class="package-dialog__video">
              <iframe
                :src="videoFor(detailsPkg)"
                frameborder="0"
                allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
              />
            </div>
          </section>
        </v-card-text>

        <v-card-actions class="package-dialog__actions">
          <v-btn text class="package-dialog__close" @click="closeDetails">Close</v-btn>
          <v-spacer />
          <span v-if="commandTargets.length === 0" class="caption text--secondary package-tile__hint mr-3">
            Select a Revit instance
          </span>
          <v-tooltip top :disabled="incompatibleVersionsFor(detailsPkg).length === 0">
            <template #activator="{ on, attrs }">
              <span v-on="on" v-bind="attrs">
                <v-btn
                  large
                  class="package-dialog__run"
                  :disabled="commandTargets.length === 0 || incompatibleVersionsFor(detailsPkg).length > 0"
                  @click="runPackage(detailsPkg)"
                >
                  Run<span class="package-tile__run-arrow">&rarr;</span>
                </v-btn>
              </span>
            </template>
            <span> Not compatible with Revit {{ incompatibleVersionsFor(detailsPkg).join(", ") }} </span>
          </v-tooltip>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      :value="!!runFeedback"
      :timeout="5000"
      color="transparent"
      elevation="0"
      bottom
      right
      content-class="run-toast"
      @input="
        (v) => {
          if (!v) runFeedback = null;
        }
      "
    >
      <div v-if="runFeedback" class="run-toast__inner">
        <span class="run-toast__pulse" />
        <div class="run-toast__text">
          <div class="run-toast__title">Sent to Revit</div>
          <div class="run-toast__sub">
            {{ runFeedback.name }} &middot; {{ runFeedback.count }} instance{{ runFeedback.count === 1 ? "" : "s" }}
            &middot; switch to Revit window
          </div>
        </div>
        <v-btn icon small @click="runFeedback = null"><v-icon small>mdi-close</v-icon></v-btn>
      </div>
    </v-snackbar>
  </v-card>
</template>

<script>
import { getXanoApiBase, postWebView2Message } from "@/utils/webview2";

function normalizeXanoPackage(row) {
  const zip = row.zip_file || {};
  return {
    packageId: row.package_id || row.package_Id || row.packageId,
    version: String(row.version || row.current_version || ""),
    displayName: row.display_name || row.displayName,
    description: row.description,
    longDescription: row.long_description || row.longDescription || null,
    instructions: row.instructions || row.steps || [],
    screenshots: row.screenshots || row.images || [],
    videoUrl: row.video_url || row.videoUrl || null,
    category: row.category || null,
    tags: row.tags || [],
    iconUrl: row.icon_url || row.iconUrl || null,
    entryType: row.entry_type || row.entryType,
    supportedRevitVersions: row.supported_revit_versions || row.supportedRevitVersions || [],
    packageUrl: row.package_url || row.packageUrl || zip.url,
  };
}

const PLACEHOLDER_INSTRUCTIONS = [
  "Select the Revit instance(s) where the script should run.",
  "Pick the elements or open the view required by the script.",
  "Click Run to send the package to Revit.",
  "Switch to the Revit window to confirm or interact with the dialog.",
];

const PLACEHOLDER_SCREENSHOTS = [
  "https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=900&q=70",
  "https://images.unsplash.com/photo-1518542698408-180bdd1e8c4f?w=900&q=70",
];

const PLACEHOLDER_VIDEO = "https://www.youtube.com/embed/dQw4w9WgXcQ";

export default {
  name: "PackageCatalog",
  props: {
    commandTargets: { type: Array, default: () => [] },
    selectedRevitVersions: { type: Array, default: () => [] },
  },
  data() {
    return {
      packages: [],
      search: "",
      selectedCategory: null,
      loading: false,
      loadError: null,
      xanoApiBase: getXanoApiBase(),
      debugXanoRaw: null,
      debugLastRunRequest: null,
      detailsPkg: null,
      runFeedback: null,
    };
  },
  mounted() {
    this.emitDebug();
    this.loadPackages();
  },
  computed: {
    categories() {
      return [...new Set(this.packages.map((p) => p.category).filter(Boolean))].sort();
    },
    filteredPackages() {
      const query = (this.search || "").toLowerCase().trim();
      return this.packages
        .filter((p) => !this.selectedCategory || p.category === this.selectedCategory)
        .filter((p) => {
          if (!query) return true;
          const haystack = [p.displayName, p.packageId, p.description, ...(p.tags || [])]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return haystack.includes(query);
        })
        .sort((a, b) => (a.displayName || a.packageId).localeCompare(b.displayName || b.packageId));
    },
  },
  methods: {
    async loadPackages() {
      if (!this.xanoApiBase) return;
      this.loading = true;
      this.loadError = null;
      try {
        const response = await fetch(this.xanoApiBase, { cache: "no-store" });
        const rawText = await response.text();
        this.debugXanoRaw = rawText;
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const body = JSON.parse(rawText);
        const list = Array.isArray(body) ? body : [];
        this.packages = list.map(normalizeXanoPackage);
      } catch (error) {
        this.loadError = error.message || String(error);
      } finally {
        this.loading = false;
        this.emitDebug();
      }
    },
    emitDebug() {
      this.$emit("debug-changed", {
        xanoApiBase: this.xanoApiBase,
        xanoRaw: this.debugXanoRaw,
        packages: this.packages,
        lastRunRequest: this.debugLastRunRequest,
      });
    },
    incompatibleVersionsFor(pkg) {
      const supported = pkg.supportedRevitVersions || [];
      if (supported.length === 0) return [];
      return this.selectedRevitVersions.filter((v) => !supported.includes(v));
    },
    runPackage(pkg) {
      if (this.incompatibleVersionsFor(pkg).length > 0) return;
      if (this.commandTargets.length === 0) return;
      const payload = {
        id: `${pkg.packageId}-${Date.now()}`,
        packageId: pkg.packageId,
        version: pkg.version,
        entryType: pkg.entryType,
        packageUrl: pkg.packageUrl,
        payloadJson: "{}",
      };
      this.debugLastRunRequest = {
        action: "run-package",
        targetInstanceIds: this.commandTargets,
        payload,
      };
      this.emitDebug();
      postWebView2Message({ action: "run-package", payload }, this.commandTargets);
      this.runFeedback = {
        name: pkg.displayName || pkg.packageId,
        count: this.commandTargets.length,
      };
      this.detailsPkg = null;
    },
    openDetails(pkg) {
      this.detailsPkg = pkg;
    },
    closeDetails() {
      this.detailsPkg = null;
    },
    instructionsFor(pkg) {
      const list = pkg && pkg.instructions;
      if (Array.isArray(list) && list.length > 0) return list;
      return PLACEHOLDER_INSTRUCTIONS;
    },
    screenshotsFor(pkg) {
      const list = pkg && pkg.screenshots;
      if (Array.isArray(list) && list.length > 0) return list;
      return PLACEHOLDER_SCREENSHOTS;
    },
    videoFor(pkg) {
      return (pkg && pkg.videoUrl) || PLACEHOLDER_VIDEO;
    },
  },
};
</script>

<style scoped>
.package-tile {
  position: relative;
  overflow: hidden;
  cursor: pointer;
  border-color: rgba(255, 255, 255, 0.08) !important;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.012), rgba(255, 255, 255, 0)) !important;
  transition: transform 260ms cubic-bezier(0.2, 0.7, 0.2, 1), border-color 220ms ease, box-shadow 260ms ease,
    background-color 220ms ease;
}

.package-tile__hint-tap {
  position: absolute;
  top: 10px;
  right: 14px;
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 0.55rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  opacity: 0;
  color: var(--accent);
  transition: opacity 240ms ease;
  z-index: 3;
  pointer-events: none;
}

.package-tile:hover .package-tile__hint-tap {
  opacity: 0.85;
}

.theme--light .package-tile {
  border-color: rgba(15, 18, 22, 0.1) !important;
  background: linear-gradient(180deg, rgba(15, 18, 22, 0.012), rgba(15, 18, 22, 0)) !important;
}

.package-tile::before {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  height: 2px;
  background: var(--accent);
  transform: scaleX(0);
  transform-origin: left center;
  transition: transform 320ms cubic-bezier(0.2, 0.7, 0.2, 1);
  z-index: 2;
}

.package-tile:hover {
  transform: translateY(-3px);
  border-color: var(--accent-line) !important;
  box-shadow: 0 14px 40px -18px rgba(232, 178, 95, 0.55), 0 2px 0 rgba(232, 178, 95, 0.08) inset;
}

.package-tile:hover::before {
  transform: scaleX(1);
}

.package-tile__corner {
  position: absolute;
  width: 14px;
  height: 14px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 240ms ease;
  z-index: 2;
}

.package-tile__corner--tl {
  top: 6px;
  left: 6px;
  border-top: 1px solid var(--accent);
  border-left: 1px solid var(--accent);
}

.package-tile__corner--br {
  bottom: 6px;
  right: 6px;
  border-bottom: 1px solid var(--accent);
  border-right: 1px solid var(--accent);
}

.package-tile:hover .package-tile__corner {
  opacity: 1;
}

.package-tile__icon {
  transition: transform 380ms cubic-bezier(0.2, 0.7, 0.2, 1);
}

.package-tile:hover .package-tile__icon {
  transform: scale(1.04);
}

.package-tile__title {
  font-family: "Fraunces", "Georgia", serif;
  font-variation-settings: "opsz" 36, "SOFT" 50;
  font-weight: 450;
  font-size: 1.2rem;
  line-height: 1.2;
  letter-spacing: -0.012em;
  word-break: normal;
}

.package-tile__meta {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 0.68rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  opacity: 0.65;
  display: flex;
  flex-wrap: wrap;
  gap: 0.45em;
  align-items: center;
}

.package-tile__version {
  color: var(--accent);
  font-weight: 600;
}

.package-tile__divider {
  opacity: 0.4;
}

.package-tile__desc {
  font-size: 0.875rem;
  line-height: 1.5;
  opacity: 0.82;
}

.package-tile__tag {
  font-family: "JetBrains Mono", ui-monospace, monospace !important;
  letter-spacing: 0.04em;
  text-transform: lowercase;
  border-color: rgba(255, 255, 255, 0.12) !important;
}

.theme--light .package-tile__tag {
  border-color: rgba(15, 18, 22, 0.18) !important;
}

.package-tile__actions {
  border-top: 1px dashed rgba(255, 255, 255, 0.06);
  padding-top: 8px;
  margin-top: 4px;
}

.theme--light .package-tile__actions {
  border-top-color: rgba(15, 18, 22, 0.08);
}

.package-tile__run {
  color: var(--accent) !important;
  font-family: "JetBrains Mono", ui-monospace, monospace !important;
  letter-spacing: 0.18em !important;
  text-transform: uppercase !important;
  font-size: 0.75rem !important;
  font-weight: 600 !important;
  padding: 0 12px !important;
}

.package-tile__run:hover {
  background: var(--accent-soft) !important;
}

.package-tile__run-arrow {
  display: inline-block;
  margin-left: 0.4em;
  transform: translateX(0);
  transition: transform 240ms cubic-bezier(0.2, 0.7, 0.2, 1);
}

.package-tile:hover .package-tile__run:not(.v-btn--disabled) .package-tile__run-arrow {
  transform: translateX(4px);
}

.package-tile__hint {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 0.65rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  opacity: 0.5;
}
</style>

<style>
.package-dialog .package-dialog__card {
  position: relative;
  background: #12161c !important;
  background-image: linear-gradient(rgba(255, 255, 255, 0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.025) 1px, transparent 1px) !important;
  background-size: 24px 24px !important;
  border: 1px solid var(--accent-line) !important;
  border-radius: 4px !important;
  overflow: hidden;
  box-shadow: 0 30px 80px -20px rgba(0, 0, 0, 0.7), 0 0 0 1px rgba(232, 178, 95, 0.08) inset !important;
}

.theme--light .package-dialog .package-dialog__card {
  background: #fbfaf6 !important;
  background-image: linear-gradient(rgba(15, 18, 22, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(15, 18, 22, 0.04) 1px, transparent 1px) !important;
  background-size: 24px 24px !important;
}

.package-dialog__corner {
  position: absolute;
  width: 22px;
  height: 22px;
  pointer-events: none;
  z-index: 2;
}

.package-dialog__corner--tl {
  top: 8px;
  left: 8px;
  border-top: 1.5px solid var(--accent);
  border-left: 1.5px solid var(--accent);
}

.package-dialog__corner--tr {
  top: 8px;
  right: 8px;
  border-top: 1.5px solid var(--accent);
  border-right: 1.5px solid var(--accent);
}

.package-dialog__hero {
  display: flex;
  gap: 1.5rem;
  padding: 2.25rem 2.25rem 1.5rem;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.08);
  position: relative;
}

.theme--light .package-dialog__hero {
  border-bottom-color: rgba(15, 18, 22, 0.1);
}

.package-dialog__icon {
  flex: 0 0 80px;
  background: rgba(232, 178, 95, 0.06);
  border: 1px solid rgba(232, 178, 95, 0.25);
  border-radius: 2px;
  padding: 8px;
}

.package-dialog__hero-text {
  flex: 1;
  min-width: 0;
}

.package-dialog__meta {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 0.68rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  opacity: 0.7;
  display: flex;
  flex-wrap: wrap;
  gap: 0.45em;
  align-items: center;
  margin-bottom: 0.5rem;
}

.package-dialog__version {
  color: var(--accent);
  font-weight: 600;
}

.package-dialog__divider {
  opacity: 0.4;
}

.package-dialog__title {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-weight: 600;
  font-size: 1.85rem;
  line-height: 1.1;
  letter-spacing: -0.018em;
  margin: 0 0 0.65rem;
}

.package-dialog__tags {
  display: flex;
  flex-wrap: wrap;
}

.package-dialog__body {
  padding: 1.75rem 2.25rem !important;
}

.package-dialog__section {
  margin-bottom: 2rem;
}

.package-dialog__section:last-child {
  margin-bottom: 0;
}

.package-dialog__h3 {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 0.7rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  opacity: 0.55;
  font-weight: 500;
  margin: 0 0 0.85rem;
  padding-left: 14px;
  position: relative;
}

.package-dialog__h3::before {
  content: "";
  position: absolute;
  left: 0;
  top: 50%;
  width: 6px;
  height: 6px;
  background: var(--accent);
  border-radius: 50%;
  transform: translateY(-50%);
}

.package-dialog__text {
  font-size: 0.95rem;
  line-height: 1.65;
  opacity: 0.88;
  margin: 0;
}

.package-dialog__steps {
  list-style: none;
  padding: 0;
  margin: 0;
  counter-reset: step;
}

.package-dialog__step {
  display: flex;
  gap: 1rem;
  padding: 0.7rem 0;
  border-bottom: 1px dashed rgba(255, 255, 255, 0.05);
}

.theme--light .package-dialog__step {
  border-bottom-color: rgba(15, 18, 22, 0.07);
}

.package-dialog__step:last-child {
  border-bottom: none;
}

.package-dialog__step-num {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  color: var(--accent);
  font-weight: 600;
  font-size: 0.85rem;
  flex: 0 0 32px;
  letter-spacing: 0.05em;
  padding-top: 2px;
}

.package-dialog__step-text {
  font-size: 0.95rem;
  line-height: 1.55;
  opacity: 0.9;
}

.package-dialog__gallery {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.75rem;
}

.package-dialog__shot {
  aspect-ratio: 16 / 10;
  background-size: cover;
  background-position: center;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 2px;
  transition: transform 240ms ease, border-color 240ms ease;
  cursor: zoom-in;
  position: relative;
}

.theme--light .package-dialog__shot {
  border-color: rgba(15, 18, 22, 0.1);
}

.package-dialog__shot:hover {
  transform: scale(1.02);
  border-color: var(--accent-line);
}

.package-dialog__video {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 2px;
  overflow: hidden;
  background: #000;
}

.theme--light .package-dialog__video {
  border-color: rgba(15, 18, 22, 0.1);
}

.package-dialog__video iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

.package-dialog__actions {
  padding: 1rem 2.25rem 1.5rem !important;
  border-top: 1px dashed rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.015);
}

.theme--light .package-dialog__actions {
  border-top-color: rgba(15, 18, 22, 0.08);
  background: rgba(15, 18, 22, 0.015);
}

.package-dialog__close {
  font-family: "JetBrains Mono", ui-monospace, monospace !important;
  letter-spacing: 0.18em !important;
  text-transform: uppercase !important;
  font-size: 0.7rem !important;
  opacity: 0.65;
}

.package-dialog__run {
  background: var(--accent) !important;
  color: #0e1116 !important;
  font-family: "JetBrains Mono", ui-monospace, monospace !important;
  letter-spacing: 0.22em !important;
  text-transform: uppercase !important;
  font-size: 0.78rem !important;
  font-weight: 700 !important;
  padding: 0 28px !important;
  height: 44px !important;
  border-radius: 2px !important;
  box-shadow: 0 8px 24px -8px rgba(232, 178, 95, 0.5) !important;
}

.package-dialog__run:hover {
  background: #f0bd6a !important;
}

.run-toast {
  padding: 0 !important;
  background: transparent !important;
  box-shadow: none !important;
}

.run-toast__inner {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  padding: 14px 14px 14px 18px;
  background: #14181f;
  border: 1px solid var(--accent-line);
  border-left: 3px solid var(--accent);
  border-radius: 3px;
  box-shadow: 0 20px 50px -20px rgba(0, 0, 0, 0.7);
  min-width: 320px;
}

.theme--light .run-toast__inner {
  background: #fbfaf6;
}

.run-toast__pulse {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: var(--accent);
  box-shadow: 0 0 0 0 var(--accent);
  animation: run-toast-pulse 1.4s ease-out infinite;
  flex: 0 0 10px;
}

@keyframes run-toast-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(232, 178, 95, 0.6);
  }
  70% {
    box-shadow: 0 0 0 12px rgba(232, 178, 95, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(232, 178, 95, 0);
  }
}

.run-toast__text {
  flex: 1;
  min-width: 0;
}

.run-toast__title {
  font-family: "JetBrains Mono", ui-monospace, monospace;
  font-size: 0.75rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--accent);
  font-weight: 600;
}

.run-toast__sub {
  font-size: 0.78rem;
  opacity: 0.75;
  margin-top: 2px;
  line-height: 1.35;
}
</style>
